import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';

/**
 * Byg din vandhane — konfigurator.
 *
 * Kunden vælger hanetype, funktioner og farve. Hvilket af de tre kompletsæt
 * (3-i-1 / 4-i-1 / 5-i-1) der rammes, udledes af forretningsreglen nedenfor —
 * kunden ser aldrig et modelnavn.
 *
 * Variantkortet kommer fra Liquid (sections/vandhane-builder.liquid). Der er
 * ingen hardcodede variant-ID'er, priser eller billed-URL'er her. Findes en
 * kombination ikke i kortet, låser builderen den af sig selv; oprettes
 * varianten senere i Shopify, låser den op uden kodeændring.
 */

/* Funktionsnøgler: K = kogende, N = nedkølet, B = brus. Rækkefølgen er fast,
   så "NK" aldrig kan opstå og ryge forbi opslaget i variantkortet. */
const FUNCS = ['K', 'N', 'B'];

/**
 * Forretningsregel (Filip, 28-08-2026):
 *   3-i-1 er forberedt til ÉN funktion, 4-i-1 til to (og er den eneste med
 *   udtræk), 5-i-1 til alle tre. Antallet af valgte funktioner afgør derfor
 *   modellen — ikke prisen, og ikke rækkefølgen i variantkortet.
 *
 * `upgrade`: kunden har bedt om en hane der kan udvides senere. 3-i-1-hardware
 * kan ikke opgraderes, så ved én funktion springes 3-i-1 over til fordel for
 * 5-i-1 (samme funktion, +500 kr, men nedkølet/brus kan kobles på bagefter).
 *
 * Returnerer modellerne i den rækkefølge de skal forsøges.
 */
function modelOrder(form, funcCount, upgrade) {
  if (form === 'UDTRAEK') return ['4-i-1'];
  if (funcCount === 1) return upgrade ? ['5-i-1'] : ['3-i-1', '5-i-1'];
  return ['5-i-1'];
}

/* Billed-fallback for kombinationer der endnu ikke har eget foto. Vi bliver
   inden for samme farve og form, så kunden altid ser den rigtige hane i den
   rigtige finish — kun funktionsmarkeringerne på hanen kan afvige. */
const IMAGE_FALLBACK = {
  NB: ['KNB', 'KN', 'N'],
  N: ['KN', 'K'],
  KN: ['KNB', 'K'],
  KNB: ['KN', 'K'],
};

class VandhaneBuilder extends Component {
  requiredRefs = ['image', 'readout', 'summary', 'amount', 'barsub', 'cta', 'alert', 'specdyn'];

  #variants = {};
  #state = { form: 'C', funcs: { K: true, N: false, B: false }, color: null, montering: false, upgrade: false };
  #prefetched = new Set();
  #busy = false;
  #ctaLabel = null;
  #confirmTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#variants = this.#readVariants();
    this.#state.color = this.#firstColor();
    this.#applyDeepLink();

    /* Ekskl. moms for erhverv. assets/ex-vat.js kan ikke bruges her: den kræver
       ?variant= i URL'en og et [data-exvat]-element, og kører kun på produktsider. */
    this.vatPercent = Number(this.dataset.vatPercent) || 25;
    this.taxesIncluded = this.dataset.taxesIncluded === 'true';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}} kr';
    this.soonLabel = this.dataset.soonLabel || 'Kommer snart';

    /* Spec-panel-tekster — alle redigerbare i temaeditoren. */
    this.specCopy = {
      /* "{funktioner}" erstattes af de funktioner der stadig kan tilføjes. */
      upgradeYes: this.dataset.specUpgradeYes || 'Ja – kan udvides med {funktioner} senere',
      upgradeNo3: this.dataset.specUpgradeNo3 || 'Nej – denne hane kan ikke udvides senere',
      upgradeFull: this.dataset.specUpgradeFull || 'Nej – alle funktioner er valgt',
      finish: this.dataset.specFinish || '',
      empty: this.dataset.specEmpty || '',
    };

    window.addEventListener('lw:customer-change', this.#onCustomerChange);
    /* close fyrer også ved Esc/tilbage-gestus, som ikke går gennem closeZoom(). */
    this.refs.lightbox?.addEventListener('close', this.#onZoomClosed);
    this.refs.lbScroll?.addEventListener('click', this.#onLightboxClick);

    this.render();
    this.#prefetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('lw:customer-change', this.#onCustomerChange);
    this.refs.lightbox?.removeEventListener('close', this.#onZoomClosed);
  }

  /* Kundetypeskift (privat/erhverv) — samme event som ex-vat.js lytter på. */
  #onCustomerChange = () => this.render();

  /* ---------- Data ---------- */

  #readVariants() {
    const node = this.querySelector('[data-lw-variants]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn('[vandhane-builder] kunne ikke læse variantdata:', err);
      return {};
    }
  }

  #firstColor() {
    const pressed = this.querySelector('[data-color]');
    return pressed ? pressed.dataset.color : null;
  }

  /**
   * Deep-link fra pdp-builder-redirect.liquid (?farve=&form=&funktion=), så
   * de omdirigerede kompletsæt-produkter lander på samme farve/funktion/pris
   * som Merchant Center-feedet forventer. Ukendte/ugyldige værdier ignoreres
   * stille — samme startopførsel som uden query-params.
   */
  #applyDeepLink() {
    const params = new URLSearchParams(window.location.search);
    const farve = params.get('farve');
    const form = params.get('form');
    const funktion = params.get('funktion');

    if (farve && [...this.querySelectorAll('[data-color]')].some((el) => el.dataset.color === farve)) {
      this.#state.color = farve;
    }
    if (form && [...this.querySelectorAll('[data-form]')].some((el) => el.dataset.form === form)) {
      this.#state.form = form;
    }
    if (funktion) {
      const funcs = { K: false, N: false, B: false };
      let any = false;
      for (const key of FUNCS) {
        if (funktion.includes(key)) {
          funcs[key] = true;
          any = true;
        }
      }
      if (any) this.#state.funcs = funcs;
    }
  }

  get #funcKey() {
    return FUNCS.filter((k) => this.#state.funcs[k]).join('');
  }

  /**
   * Slår den valgte konfiguration op. Returnerer enten en variant eller en
   * grund til at den ikke findes — aldrig bare null, så UI'et altid kan
   * forklare kunden hvorfor noget er låst.
   */
  #resolve(state = this.#state) {
    const key = FUNCS.filter((k) => state.funcs[k]).join('');
    const count = key.length;

    if (!count) return { error: 'none' };
    if (state.form === 'UDTRAEK' && state.funcs.B) return { error: 'brus_udtraek' };
    /* Brus leveres kun sammen med nedkøling. Reglen står eksplicit her frem for
       at blive udledt af manglende varianter — ellers ville brus alene og
       kogende+brus blive låst med "Kommer snart", og de kommer ikke. */
    if (state.funcs.B && !state.funcs.N) return { error: 'brus_kraever_nedkolet' };

    for (const model of modelOrder(state.form, count, state.upgrade)) {
      const found = this.#variants[`${model}|${state.color}|${state.form}|${key}`];
      if (found && found.a) return { key, model, variant: found, image: this.#image(state, key, model) };
    }

    return { error: 'soon', key };
  }

  /** Billed-URL for en konfiguration, med fallback inden for samme farve/form. */
  #image(state, key, model) {
    const direct = this.#variants[`${model}|${state.color}|${state.form}|${key}`];
    if (direct?.g) return direct.g;

    for (const alt of IMAGE_FALLBACK[key] || []) {
      for (const m of ['5-i-1', '4-i-1', '3-i-1']) {
        const hit = this.#variants[`${m}|${state.color}|${state.form}|${alt}`];
        if (hit?.g) return hit.g;
      }
    }
    return null;
  }

  /** Kan denne funktion overhovedet vælges til, givet resten af valget? */
  #funcAvailable(func) {
    const next = {
      ...this.#state,
      funcs: { ...this.#state.funcs, [func]: !this.#state.funcs[func] },
    };
    if (!FUNCS.some((k) => next.funcs[k])) return { ok: false, reason: '' };
    return this.#resolve(next).error ? { ok: false, reason: this.#lockReason(next) } : { ok: true };
  }

  #lockReason(state) {
    if (state.form === 'UDTRAEK' && state.funcs.B) return 'Fås ikke med udtrækshane';
    if (state.funcs.B && !state.funcs.N) return 'Kræver nedkølet vand';
    return this.soonLabel;
  }

  /**
   * Skal opgraderbar-checkboxen vises? Kun når valget ellers lander på 3-i-1 —
   * dvs. præcis én funktion, ingen udtræk, og en tilgængelig 3-i-1-variant
   * findes for farve+funktion. Uden en 3-i-1-variant falder valget allerede
   * til 5-i-1, og checkboxen ville ikke gøre noget.
   */
  #upgradeRelevant() {
    const s = this.#state;
    if (s.form === 'UDTRAEK') return false;
    const key = FUNCS.filter((k) => s.funcs[k]).join('');
    if (key.length !== 1) return false;
    return Boolean(this.#variants[`3-i-1|${s.color}|${s.form}|${key}`]?.a);
  }

  /* ---------- Handlinger ---------- */

  selectForm(event) {
    const button = event.target.closest('[data-form]');
    if (!button) return;
    this.#state.form = button.dataset.form;
    /* Brus findes ikke på udtrækshanen — slå den fra frem for at efterlade
       kunden i en tilstand der ikke kan lægges i kurv. */
    if (this.#state.form === 'UDTRAEK') this.#state.funcs.B = false;
    this.render();
    this.#prefetch();
  }

  toggleFunc(event) {
    const button = event.target.closest('[data-func]');
    if (!button || button.disabled) return;
    const key = button.dataset.func;

    this.#state.funcs[key] = !this.#state.funcs[key];
    /* Brus falder med når nedkøling slås fra — ellers står kunden i en tilstand
       der ikke kan lægges i kurv. Samme mønster som selectForm() bruger for udtræk. */
    if (key === 'N' && !this.#state.funcs.N) this.#state.funcs.B = false;
    /* Mindst én funktion skal være slået til — ellers er der ingen pris at vise. */
    if (!FUNCS.some((k) => this.#state.funcs[k])) this.#state.funcs[key] = true;

    this.render();
    this.#prefetch();
  }

  selectColor(event) {
    const button = event.target.closest('[data-color]');
    if (!button) return;
    this.#state.color = button.dataset.color;
    this.render();
  }

  toggleMontering() {
    this.#state.montering = !this.#state.montering;
    this.render();
  }

  toggleUpgrade() {
    this.#state.upgrade = !this.#state.upgrade;
    this.render();
    this.#prefetch();
  }

  /* ---------- Forstør billede ---------- */

  openZoom() {
    const { image, lightbox, lbImg } = this.refs;
    if (!image.dataset.src || !lightbox) return;

    /* Vis det allerede indlæste billede straks og skift til høj opløsning,
       når det er hentet — ellers står kunden med en tom skærm på mobilnet. */
    lbImg.alt = image.alt;
    lbImg.src = image.currentSrc || image.src;
    const full = this.#src(image.dataset.src, 2000);
    const hires = new Image();
    hires.onload = () => {
      if (lightbox.open) lbImg.src = full;
    };
    hires.src = full;

    lightbox.removeAttribute('data-zoomed');
    lightbox.showModal();
    this.refs.lbScroll.scrollTo(0, 0);
  }

  closeZoom() {
    this.refs.lightbox?.close();
  }

  #onZoomClosed = () => {
    this.refs.lightbox.removeAttribute('data-zoomed');
    this.refs.lbImg.removeAttribute('src');
  };

  /* Native listener (ikke on:click): den deklarative handler får containerens
     element som event.target, så vi kan ikke se om trykket ramte billedet. */
  #onLightboxClick = (event) => {
    const { lightbox, lbScroll, lbImg } = this.refs;
    if (event.target !== lbImg) {
      this.closeZoom();
      return;
    }

    const rect = lbImg.getBoundingClientRect();
    const fx = (event.clientX - rect.left) / rect.width;
    const fy = (event.clientY - rect.top) / rect.height;
    const zoomed = lightbox.toggleAttribute('data-zoomed');

    if (zoomed) {
      lbScroll.scrollLeft = fx * lbScroll.scrollWidth - lbScroll.clientWidth / 2;
      lbScroll.scrollTop = fy * lbScroll.scrollHeight - lbScroll.clientHeight / 2;
    }
  };

  /* ---------- Pris ---------- */

  get #erhverv() {
    return document.documentElement.dataset.customer === 'erhverv';
  }

  /* Samme formel som ex-vat.js:43-47, så de to steder ikke kan drive fra hinanden. */
  #applyVat(cents) {
    if (!this.#erhverv || !this.taxesIncluded) return cents;
    return Math.round((cents * 100) / (100 + this.vatPercent));
  }

  #money(cents) {
    const abs = Math.abs(cents);
    const kr = Math.floor(abs / 100);
    const ore = String(abs % 100).padStart(2, '0');
    const grouped = String(kr).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const amount = ore === '00' ? grouped : `${grouped},${ore}`;
    const placeholder = this.moneyFormat.match(/\{\{\s*[^}]+\s*\}\}/);
    return placeholder ? this.moneyFormat.replace(placeholder[0], amount) : `${amount} kr`;
  }

  /* ---------- Render ---------- */

  render() {
    /* Opgraderbar-valget nulstilles når det ikke er relevant (fx to funktioner
       valgt), så et gammelt flueben ikke smitter af på en senere tilstand.
       Sker før #resolve(), så opslaget ser den rigtige model. */
    const upgradeRelevant = this.#upgradeRelevant();
    if (!upgradeRelevant) this.#state.upgrade = false;

    const result = this.#resolve();

    this.#paintForms();
    this.#paintFuncs();
    this.#paintColors();

    this.refs.montering?.setAttribute('aria-pressed', String(this.#state.montering));

    if (this.refs.upgrade) this.refs.upgrade.hidden = !upgradeRelevant;
    this.refs.upgradeBtn?.setAttribute('aria-pressed', String(this.#state.upgrade));

    if (result.error) {
      this.#paintError(result);
      this.#paintSpecs(result);
      this.#paintLeje(result);
      return;
    }

    this.refs.alert.hidden = true;
    this.refs.flag.hidden = true;

    this.#paintImage(result);
    this.#paintReadout();
    this.#paintSummary(result);
    this.#paintBar(result);
    this.#paintSpecs(result);
    this.#paintLeje(result);
  }

  #paintForms() {
    for (const button of this.querySelectorAll('[data-form]')) {
      button.setAttribute('aria-pressed', String(button.dataset.form === this.#state.form));
    }
  }

  #paintFuncs() {
    for (const button of this.querySelectorAll('[data-func]')) {
      const key = button.dataset.func;
      const on = this.#state.funcs[key];
      button.setAttribute('aria-pressed', String(on));

      const lock = button.querySelector('[data-lock]');
      /* En aktiv funktion låses aldrig — kunden skal altid kunne fortryde. */
      const check = on ? { ok: true } : this.#funcAvailable(key);
      button.disabled = !check.ok;

      if (lock) {
        lock.textContent = check.ok ? '' : check.reason;
        lock.hidden = check.ok || !check.reason;
      }
    }
  }

  #paintColors() {
    for (const button of this.querySelectorAll('[data-color]')) {
      button.setAttribute('aria-pressed', String(button.dataset.color === this.#state.color));
    }
  }

  /**
   * Shopify-billed-URL i en given bredde.
   * Liquids image_url lægger allerede &width=1200 på, så den skal fjernes
   * først — ellers står der to width-parametre i URL'en.
   */
  #src(url, width) {
    return `${url.replace(/&width=\d+/, '')}&width=${width}`;
  }

  #paintImage(result) {
    const { image } = this.refs;
    if (!result.image || image.dataset.src === result.image) return;

    image.dataset.src = result.image;
    image.setAttribute('data-swapping', '');

    const full = this.#src(result.image, 900);
    const next = new Image();
    const done = () => image.removeAttribute('data-swapping');

    next.onload = () => {
      image.src = full;
      image.srcset = [
        `${this.#src(result.image, 500)} 500w`,
        `${full} 900w`,
        `${this.#src(result.image, 1200)} 1200w`,
      ].join(', ');
      image.sizes = '(min-width: 720px) 46vw, 94vw';
      image.alt = this.#altText();
      if (this.refs.zoomBtn) this.refs.zoomBtn.hidden = false;
      done();
    };
    next.onerror = done;
    next.src = full;
  }

  #formLabel() {
    const button = this.querySelector(`[data-form="${this.#state.form}"] .lw-vb__formname`);
    return button ? button.textContent.trim() : this.#state.form;
  }

  #funcLabels() {
    return FUNCS.filter((k) => this.#state.funcs[k]).map((k) => {
      const node = this.querySelector(`[data-func="${k}"] .lw-vb__funcname`);
      return node ? node.textContent.trim() : k;
    });
  }

  #altText() {
    return `${this.#state.color} ${this.#formLabel().toLowerCase()} vandhane fra Living Water med ${this.#funcLabels()
      .join(', ')
      .toLowerCase()}`;
  }

  #paintReadout() {
    const dots = FUNCS.filter((k) => this.#state.funcs[k])
      .map((k) => {
        const tone = { K: 'hot', N: 'cold', B: 'fizz' }[k];
        const node = this.querySelector(`[data-func="${k}"] .lw-vb__funcname`);
        const label = node ? node.textContent.trim() : k;
        return `<span><i class="lw-vb__dot" style="background:var(--lw-${tone})"></i>${label}</span>`;
      })
      .join('');

    this.refs.readout.innerHTML = `<strong>${this.#state.color}</strong><strong>${this.#formLabel()}</strong>${dots}`;
  }

  #paintSummary(result) {
    const price = this.#applyVat(result.variant.p);
    const rows = [
      ['Hane', `${this.#formLabel()}, ${this.#state.color}`],
      ['Funktioner', this.#funcLabels().join(', ')],
      ['Filter', 'Inkluderet'],
      ['Beholder', '2,4 liter'],
    ];

    let html = rows
      .map(([term, value]) => `<div class="lw-vb__row"><dt>${term}</dt><dd>${value}</dd></div>`)
      .join('');

    html += `<div class="lw-vb__row lw-vb__row--total"><dt>Pris${
      this.#erhverv ? ' ekskl. moms' : ''
    }</dt><dd>${this.#money(price)}</dd></div>`;

    if (this.#state.montering && !this.#erhverv) {
      const note = this.refs.montering.querySelector('.lw-vb__montprice');
      html += `<div class="lw-vb__row lw-vb__row--note"><dt>Professionel montering</dt><dd>${
        note ? note.textContent.trim() : ''
      }</dd></div>`;
    }

    this.refs.summary.innerHTML = html;
  }

  #paintBar(result) {
    const price = this.#applyVat(result.variant.p);
    this.refs.amount.textContent = this.#money(price);
    this.refs.barsub.textContent = this.#erhverv
      ? 'Ekskl. moms · fri fragt'
      : 'Inkl. moms · fri fragt';
    this.refs.cta.disabled = false;
  }

  /**
   * Leje via Grenke. Priserne (l24/l36/l60/rv) er allerede ekskl. moms i
   * variantkortet — #applyVat() må IKKE bruges her, den ville trække moms
   * fra en gang til for erhverv. Boksen er altid i markup'en; skjules pr.
   * valgt kombination hvis den ikke har lejepris (fx udsolgt, "Kommer snart",
   * eller en funktion/farve-kombination uden leje sat i admin).
   */
  #paintLeje(result) {
    const { leje } = this.refs;
    if (!leje) return;

    const variant = result.variant;
    if (!variant || variant.l24 == null) {
      leje.hidden = true;
      return;
    }
    leje.hidden = false;

    this.refs.lejeFrom.textContent = this.#money(variant.l60);
    this.refs.leje24.textContent = `${this.#money(variant.l24)}/md.`;
    this.refs.leje36.textContent = `${this.#money(variant.l36)}/md.`;
    this.refs.leje60.textContent = `${this.#money(variant.l60)}/md.`;
    this.refs.lejeRv.textContent = this.#money(variant.rv);

    if (this.refs.lejeDetails) this.refs.lejeDetails.open = this.#erhverv;

    if (this.refs.lejeCta) {
      const produkt = `Vandhane (${this.#formLabel()}, ${this.#state.color}, ${this.#funcLabels().join('+')})`;
      const url = new URL(this.refs.lejeCta.href, window.location.origin);
      url.searchParams.set('emne', 'leje');
      url.searchParams.set('produkt', produkt);
      this.refs.lejeCta.href = `${url.pathname}${url.search}`;
    }
  }

  /** GA4/GTM: sporer klik på "Anmod om lejetilbud" i builderen. Ses straks i
   * GTM Preview; kræver en GA4-tag+trigger i selve GTM-containeren for at nå GA4. */
  trackLejeCta() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'leje_cta_klik',
      produkt: `Vandhane (${this.#formLabel()}, ${this.#state.color}, ${this.#funcLabels().join('+')})`,
      kilde: 'byg-din-vandhane',
    });
  }

  #funcName(k) {
    const node = this.querySelector(`[data-func="${k}"] .lw-vb__funcname`);
    return node ? node.textContent.trim() : k;
  }

  /**
   * "Kan opgraderes senere"-teksten. Afhænger af model OG hvad der allerede er
   * valgt:
   *   3-i-1  — kan intet. Hardwaren er låst.
   *   4-i-1  — kan tilføje den anden af kogende/nedkølet. Aldrig brus (udtræk).
   *   5-i-1  — kan tilføje alt der ikke er valgt (brus forudsætter nedkølet,
   *            men nedkølet tilbydes i samme sætning).
   */
  #upgradeInfo(result) {
    if (result.error) return '';
    if (result.model === '3-i-1') return this.specCopy.upgradeNo3;

    const pool = result.model === '4-i-1' ? ['K', 'N'] : ['K', 'N', 'B'];
    const missing = pool.filter((k) => !this.#state.funcs[k]);
    if (!missing.length) return this.specCopy.upgradeFull;

    const names = missing.map((k) => this.#funcName(k).toLowerCase());
    const list =
      names.length > 1
        ? `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`
        : names[0];
    return this.specCopy.upgradeYes.replace('{funktioner}', list);
  }

  /**
   * Fuldt spec-panel nederst. Base-listen (temperatur, effekt, mål osv.) kommer
   * fra produktets metafelt og skiftes efter hvilken model valget rammer; de
   * dynamiske rækker ovenover bygges her ud fra kundens valg.
   */
  #paintSpecs(result) {
    const model = result.error ? '5-i-1' : result.model;

    for (const node of this.querySelectorAll('[data-spec-base]')) {
      node.hidden = node.dataset.specBase !== model;
    }

    if (result.error) {
      this.refs.specdyn.innerHTML = this.specCopy.empty
        ? `<div class="lw-vb__row lw-vb__row--note"><dt>Status</dt><dd>${this.specCopy.empty}</dd></div>`
        : '';
      return;
    }

    const price = this.#applyVat(result.variant.p);
    const rows = [
      ['Konfiguration', model],
      ['Hanetype', this.#formLabel()],
      ['Funktioner', this.#funcLabels().join(', ')],
      ['Kan opgraderes senere', this.#upgradeInfo(result)],
      ['Finish', [this.#state.color, this.specCopy.finish].filter(Boolean).join(' · ')],
      [`Pris${this.#erhverv ? ' ekskl. moms' : ''}`, this.#money(price)],
    ];

    this.refs.specdyn.innerHTML = rows
      .filter(([, value]) => value)
      .map(([term, value]) => `<div class="lw-vb__row"><dt>${term}</dt><dd>${value}</dd></div>`)
      .join('');
  }

  #paintError(result) {
    const messages = {
      none: 'Vælg mindst én funktion for at se pris.',
      brus_udtraek: 'Brus fås ikke med udtrækshane. Vælg C- eller U-tud, eller slå brus fra.',
      brus_kraever_nedkolet:
        'Brus fås kun sammen med nedkølet vand. Slå nedkølet til, eller slå brus fra.',
      soon: `Den kombination kan endnu ikke bestilles online. Ring til os på 25 12 16 17, så finder vi den rigtige løsning.`,
    };

    this.refs.alert.hidden = false;
    this.refs.alert.textContent = messages[result.error] || messages.soon;
    this.refs.amount.innerHTML = '&mdash;';
    this.refs.barsub.textContent = messages[result.error] || '';
    this.refs.cta.disabled = true;
    this.refs.summary.innerHTML = '<div class="lw-vb__row"><dt>Status</dt><dd>Ufuldstændig</dd></div>';

    if (result.error === 'soon') {
      this.refs.flag.hidden = false;
      this.refs.flag.textContent = this.soonLabel;
    } else {
      this.refs.flag.hidden = true;
    }
  }

  /* ---------- Kurv ---------- */

  async addToCart() {
    if (this.#busy) return;
    const result = this.#resolve();
    if (result.error) return;

    const items = [{ id: result.variant.i, quantity: 1 }];
    /* Erhverv får forhandlet montering (kontakt-tekst), aldrig fast pris i kurv.
       Læses ved add-to-cart-tid, så et skift af kundetype altid slår igennem. */
    const monteringId = this.dataset.monteringVariant;
    if (this.#state.montering && monteringId && !this.#erhverv) {
      items.push({ id: monteringId, quantity: 1 });
    }

    this.#busy = true;
    this.#ctaLabel ??= this.refs.cta.textContent;
    this.refs.cta.disabled = true;

    try {
      const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const cart = await response.json();

      if (!response.ok || cart.status) {
        /* 422 = varianten kan ikke lægges i kurv. Næsten altid et
           publiceringsproblem i Shopify, ikke en fejl i denne kode. */
        if (response.status === 422) {
          console.warn(
            `[vandhane-builder] Variant ${result.variant.i} kan ikke lægges i kurv (422). ` +
              'Er produktet publiceret til Webshop-salgskanalen?',
            cart
          );
        } else {
          console.warn('[vandhane-builder] kurv-fejl:', cart);
        }
        this.refs.alert.hidden = false;
        this.refs.alert.textContent = 'Kunne ikke lægge i kurv. Prøv igen, eller ring til os på 25 12 16 17.';
        return;
      }

      /* Opdaterer kurv-badge og kurv-indhold. Draweren åbner kun af sig selv
         hvis temaindstillingen "åbn kurv automatisk" er slået til — den er
         slået fra i denne shop, så knappen kvitterer selv nedenfor. */
      document.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'product-form-component',
          itemCount: items.length,
        })
      );

      this.#confirm();
    } catch (error) {
      console.warn('[vandhane-builder] netværksfejl ved kurv:', error);
      this.refs.alert.hidden = false;
      this.refs.alert.textContent = 'Ingen forbindelse. Prøv igen om et øjeblik.';
    } finally {
      this.#busy = false;
      this.refs.cta.disabled = Boolean(this.#resolve().error);
    }
  }

  /**
   * Kvittering i knappen. Nødvendig fordi kurv-draweren ikke åbner automatisk
   * i denne shop — uden den får kunden ingen bekræftelse ud over at
   * kurv-badgen tælles op oppe i headeren.
   */
  #confirm() {
    const { cta } = this.refs;
    cta.textContent = 'Lagt i kurv';
    cta.dataset.done = '';
    clearTimeout(this.#confirmTimer);
    this.#confirmTimer = setTimeout(() => {
      cta.textContent = this.#ctaLabel;
      delete cta.dataset.done;
    }, 2200);
  }

  /* ---------- Prefetch af nabofarver ---------- */

  #prefetch() {
    /* Respektér datasparetilstand — builderen skal ikke koste mobildata. */
    if (navigator.connection?.saveData) return;

    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 400));
    idle(() => {
      const key = this.#funcKey;
      if (!key) return;

      for (const button of this.querySelectorAll('[data-color]')) {
        const color = button.dataset.color;
        if (color === this.#state.color) continue;

        const src = this.#image(
          { ...this.#state, color },
          key,
          modelOrder(this.#state.form, key.length, this.#state.upgrade)[0]
        );
        if (!src) continue;

        const url = this.#src(src, 500);
        if (this.#prefetched.has(url)) continue;
        this.#prefetched.add(url);

        const image = new Image();
        image.decoding = 'async';
        image.src = url;
      }
    });
  }
}

if (!customElements.get('vandhane-builder')) {
  customElements.define('vandhane-builder', VandhaneBuilder);
}
